import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../../config';
import { useDomainValidation } from '../../hooks/useDomainValidation';
import DomainValidationModal from '../DomainValidationRetryModal';
import { CitationChip } from '../Citations';

import { useEscapeToClose } from '../../hooks/useEscapeToClose';
function PrescriptionForm({ onClose, onSave, fetchWithAuth, editingPrescription, defaultItemType = 'prescription', prefillData = null }) {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const { domainValidation, handleValidationResponse, clearValidation } = useDomainValidation();
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimerRef = useRef(null);
  const itemTypeRef = useRef(defaultItemType);

  const handleNameSearch = (value) => {
    setFormData(prev => ({...prev, medication_name: value}));
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (value.length < 2) { setSearchResults([]); setShowSuggestions(false); return; }
    searchTimerRef.current = setTimeout(async () => {
      try {
        const currentType = itemTypeRef.current;
        const searchType = currentType === 'supplement' ? 'supplement' : currentType === 'prescription' ? 'prescription' : 'all';
        const resp = await fetchWithAuth(`${API_BASE}/prescriptions/search?q=${encodeURIComponent(value)}&type=${searchType}`);
        if (resp.ok) {
          const data = await resp.json();
          setSearchResults(data.results || []);
          setShowSuggestions(data.results && data.results.length > 0);
        }
      } catch (err) { console.error('Autocomplete search error:', err); }
    }, 300);
  };

  const selectSuggestion = (item) => {
    setFormData(prev => ({...prev, medication_name: item.name}));
    setShowSuggestions(false);
    setSearchResults([]);
  };

  const [formData, setFormData] = useState({
    medication_name: '',
    dosage: '',
    frequency: '',
    prescribing_doctor: '',
    start_date: '',
    end_date: '',
    notes: '',
    is_active: true,
    item_type: defaultItemType,
    category: '',
    timing_instructions: '',
    reason: ''
  });
  const [saveWarnings, setSaveWarnings] = useState(null);

  const categoryOptions = {
    prescription: ['antidepressant', 'statin', 'blood_thinner', 'beta_blocker', 'ace_inhibitor', 'antibiotic', 'diabetes', 'thyroid', 'pain', 'immunosuppressant', 'other'],
    otc: ['pain_reliever', 'antacid', 'antihistamine', 'decongestant', 'sleep_aid', 'other'],
    supplement: ['vitamin', 'mineral', 'herbal', 'amino_acid', 'probiotic', 'omega', 'enzyme', 'other']
  };

  // Populate form with editing data or AI prefill
  useEffect(() => {
    if (editingPrescription) {
      const data = {
        medication_name: editingPrescription.medication_name || '',
        dosage: editingPrescription.dosage || '',
        frequency: editingPrescription.frequency || '',
        prescribing_doctor: editingPrescription.prescribing_doctor || '',
        start_date: editingPrescription.start_date ?
          new Date(editingPrescription.start_date).toISOString().split('T')[0] : '',
        end_date: editingPrescription.end_date ?
          new Date(editingPrescription.end_date).toISOString().split('T')[0] : '',
        notes: editingPrescription.notes || '',
        is_active: editingPrescription.is_active !== undefined ? editingPrescription.is_active : true,
        item_type: editingPrescription.item_type || 'prescription',
        category: editingPrescription.category || '',
        timing_instructions: editingPrescription.timing_instructions || '',
        reason: editingPrescription.reason || ''
      };
      setFormData(data);
      itemTypeRef.current = data.item_type;
    } else if (prefillData) {
      const data = {
        medication_name: prefillData.medication_name || '',
        dosage: prefillData.dosage || '',
        frequency: '',
        prescribing_doctor: '',
        start_date: '',
        end_date: '',
        notes: '',
        is_active: true,
        item_type: prefillData.item_type || 'supplement',
        category: prefillData.category || '',
        timing_instructions: prefillData.timing_instructions || '',
        reason: prefillData.reason || ''
      };
      setFormData(data);
      itemTypeRef.current = data.item_type;
    }
  }, [editingPrescription, prefillData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Convert empty strings to null for optional fields
    const submitData = {
      ...formData,
      start_date: formData.start_date === '' ? null : formData.start_date,
      end_date: formData.end_date === '' ? null : formData.end_date,
      category: formData.category === '' ? null : formData.category,
      timing_instructions: formData.timing_instructions === '' ? null : formData.timing_instructions,
      reason: formData.reason === '' ? null : formData.reason,
      prescribing_doctor: formData.prescribing_doctor === '' ? null : formData.prescribing_doctor,
      notes: formData.notes === '' ? null : formData.notes,
    };

    try {
      const url = editingPrescription
        ? `${API_BASE}/prescriptions/${editingPrescription.id}`
        : `${API_BASE}/prescriptions/`;

      const method = editingPrescription ? 'PUT' : 'POST';

      const response = await fetchWithAuth(url, {
        method: method,
        body: JSON.stringify(submitData)
      });

      const validationResult = await handleValidationResponse(response, 'prescription');
      if (validationResult === true) {
        // Check for interactions after save
        try {
          const ixResp = await fetchWithAuth(`${API_BASE}/prescriptions/interactions`);
          if (ixResp.ok) {
            const ixData = await ixResp.json();
            if (ixData.summary && ixData.summary.total > 0) {
              setSaveWarnings(ixData);
            } else {
              onSave();
              onClose();
            }
          } else {
            onSave();
            onClose();
          }
        } catch {
          onSave();
          onClose();
        }
      }
    } catch (error) {
      console.error('Failed to save prescription:', error);
    }
  };

  const dismissWarnings = () => {
    setSaveWarnings(null);
    onSave();
    onClose();
  };

  const isSupplementType = formData.item_type === 'supplement' || formData.item_type === 'otc';
  const typeLabel = formData.item_type === 'supplement' ? 'Supplement' : formData.item_type === 'otc' ? 'OTC Medication' : 'Prescription';

  return (
    <>{domainValidation && (
      <DomainValidationModal
        isOpen={true}
        mode={domainValidation.mode}
        rejectionMessage={domainValidation.message}
        entityType={domainValidation.entityType}
        onClose={clearValidation}
      />
    )}
    {saveWarnings ? (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3>Interaction Warnings</h3>
            <button className="modal-close" onClick={dismissWarnings}>&times;</button>
          </div>
          <div className="med-warningBanner">
            <p><strong>Interactions detected</strong> with your current medications/supplements:</p>
            {[...(saveWarnings.supplement_drug || []), ...(saveWarnings.supplement_supplement || [])].map((ix, i) => (
              <div key={i} className={`med-warningItem med-severity-${ix.severity}`}>
                <span>{ix.severity === 'critical' ? '🔴' : ix.severity === 'significant' ? '🟠' : '🟡'} </span>
                <strong>{ix.supplement || ix.supplement_a}</strong> + <strong>{ix.medication || ix.supplement_b}</strong>
                <p>{ix.mechanism}</p>
                {/* Apple 1.4.1 (G4): this warning's sources, one tap away. */}
                <CitationChip sources={ix.sources} featureName="Medication Interaction Check"
                              itemTitle={`${ix.supplement || ix.supplement_a} + ${ix.medication || ix.supplement_b}`} />
              </div>
            ))}
            <p className="med-warningDisclaimer">Your item was saved successfully. Please consult your healthcare provider about these interactions.</p>
          </div>
          <div className="flex gap-2" style={{marginTop: '1rem'}}>
            <button className="btn btn-primary" onClick={dismissWarnings}>Acknowledge</button>
          </div>
        </div>
      </div>
    ) : (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3>{editingPrescription ? `Edit ${typeLabel}` : `Add ${typeLabel}`}</h3>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Type *</label>
              <select
                className="form-input"
                value={formData.item_type}
                onChange={(e) => { itemTypeRef.current = e.target.value; setFormData(prev => ({...prev, item_type: e.target.value, category: ''})); }}
              >
                <option value="prescription">Prescription (Rx)</option>
                <option value="otc">Over-the-Counter (OTC)</option>
                <option value="supplement">Supplement</option>
              </select>
            </div>

            <div className="form-group" style={{position: 'relative'}}>
              <label className="form-label">{isSupplementType ? 'Supplement Name' : 'Medication Name'} *</label>
              <input
                type="text"
                className="form-input"
                value={formData.medication_name}
                onChange={(e) => handleNameSearch(e.target.value)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                onFocus={() => searchResults.length > 0 && setShowSuggestions(true)}
                placeholder={isSupplementType ? 'e.g. Vitamin D3, Fish Oil, Magnesium' : 'e.g. Metformin, Lisinopril'}
                autoComplete="off"
                required
              />
              {showSuggestions && searchResults.length > 0 && (
                <div className="med-autocomplete">
                  {searchResults.map((item, idx) => (
                    <div
                      key={idx}
                      className="med-autocompleteItem"
                      onMouseDown={() => selectSuggestion(item)}
                    >
                      <span className="med-autocompleteName">{item.name}</span>
                      {item.generic && <span className="med-autocompleteGeneric">({item.generic})</span>}
                      <span className="med-autocompleteSource">{item.source === 'openfda' ? 'FDA' : item.source === 'supplement_db' ? 'Supplement' : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                className="form-input"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({...prev, category: e.target.value}))}
              >
                <option value="">Select category...</option>
                {(categoryOptions[formData.item_type] || []).map(cat => (
                  <option key={cat} value={cat}>{cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Dosage</label>
              <input
                type="text"
                className="form-input"
                value={formData.dosage}
                onChange={(e) => setFormData(prev => ({...prev, dosage: e.target.value}))}
                placeholder="e.g. 500mg, 1000 IU, 2 capsules"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Frequency</label>
              <input
                type="text"
                className="form-input"
                value={formData.frequency}
                onChange={(e) => setFormData(prev => ({...prev, frequency: e.target.value}))}
                placeholder="e.g. Twice daily, As needed, Once weekly"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Timing Instructions</label>
              <input
                type="text"
                className="form-input"
                value={formData.timing_instructions}
                onChange={(e) => setFormData(prev => ({...prev, timing_instructions: e.target.value}))}
                placeholder="e.g. Take with food, Take on empty stomach, Take 2 hours after calcium"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Reason for Taking</label>
              <input
                type="text"
                className="form-input"
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({...prev, reason: e.target.value}))}
                placeholder="e.g. Bone health, Blood pressure management, Energy support"
              />
            </div>

            {formData.item_type === 'prescription' && (
              <div className="form-group">
                <label className="form-label">Prescribing Doctor</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.prescribing_doctor}
                  onChange={(e) => setFormData(prev => ({...prev, prescribing_doctor: e.target.value}))}
                  placeholder="e.g. Dr. Smith"
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-input"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({...prev, start_date: e.target.value}))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-input"
                value={formData.end_date}
                onChange={(e) => setFormData(prev => ({...prev, end_date: e.target.value}))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-textarea"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
                placeholder="Additional notes"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({...prev, is_active: e.target.checked}))}
                />
                Currently Active
              </label>
            </div>

            <div className="flex gap-2">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {editingPrescription ? `Update ${typeLabel}` : `Save ${typeLabel}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}</>
  );
}

export default PrescriptionForm;
